import { useEffect, useRef, useState } from 'react';
import { useImmerReducer } from 'use-immer';

import { addPowerUpAction, decreasePowerUpAction, endGameAction, extendGameAction, loadGameAction, removePowerUpAction, rushGameInitialState, rushGameReducer, RushGameState, setAmountAction, setLeverageAction, startGameAction, tickGameAction } from '../reducers/rush-game.reducer'; // Import the reducer and action creator

import TradingAmount from "../components/rushGame/trading-amount.component";
import { SecondaryNavigation } from "../components/ui/secondary-navigation.component";
import Layout from "../Layout";
import Leverage from '../components/rushGame/leverage.component';
import { PowerUpSelector } from '../components/rushGame/power-up.component';
import CircularCountDown from '../components/rushGame/circular-countdown.component';
import GameControls from '../components/rushGame/game-controls.component';
import RushChart from '../components/rushGame/rush-chart.component';
import { GroupInventoryItem } from '../models/User';

import { useGetInventoryQuery, useGetUserNotificationsQuery, useGetUserQuery } from '../apis/users.api';

import { addGameSocketListeners } from '../utils/GameSocketHelper';
import { toastCustomError } from '../utils/ToastUtils';
import { useGetMoneyAmountQuery } from '../apis/game-income.api';
import { InitialOnboardingDrawer } from '../components/tutorial/onboarding/initial-onboarding-drawer.component';

interface RushGamePageProps {
  socket: any
}

const RushGamePage: React.FC<RushGamePageProps> = ({socket}) => {

  const rushChartRef = useRef<any>();

  const [openOnboarding, setOpenOnboarding] = useState(false);

  const { data: user, isLoading: isUserLoading, refetch: refetchUser } = useGetUserQuery();
  const { data: money, isLoading: isMoneyLoading, refetch: refetechMoney } = useGetMoneyAmountQuery();
  const { data: notificationsPayload, refetch: refetchNotifications } = useGetUserNotificationsQuery();
  const { data: inventory, isLoading: isInventoryLoading, refetch:refetechInventory } = useGetInventoryQuery({});

  useEffect(() => {
    const open = !isUserLoading && (!user?.onBoarding?.find(o => o.tag === 'initial') || !user?.onBoarding?.find(o => o.tag === 'initial')?.value);
    setOpenOnboarding(open);
  }, [user]);

  useEffect(() => {
    if (!isUserLoading && user) {

      socket.auth.userId = user._id;
      socket.disconnect().connect();

      socket.emit("join-game", { userId: user._id }, (runningSession: any) => {
        // TODO: if a game session is running - initialize reducer
        console.log("join-game result: ", runningSession ? runningSession : "no runningSession");
        if (runningSession){
          loadGameDispatch(runningSession);
        }
      });

      addGameSocketListeners(socket); // TODO: isolate socket listeners

      socket.on("game-start", (gameSession: any) => {

        console.log("game-start: ", gameSession);

        // rushChartRef.current.loadFn(rushGameState);

        loadGameDispatch(gameSession);
        // rushChartRef.current.start(gameSession.isTradingUp);

        // dispatch(startGameAction(isTradingUp, socket));
        refetechMoney();

      });

      socket.on("game-tick", (data: any) => {

        // console.log("game-tick: ", data);

        // Reduce timer by one
        tickGameDispatch(data.tradePrice);

        rushChartRef.current.initBTCPrices(data.lastBTCPrices);

        // Update chart datas
        rushChartRef.current.updateCurrentValue(data.tradePrice);

      });

      socket.on("game-end", async (response:any) => {

        if (response && response.reason === 'no-enough-money'){
          toastCustomError("You don't have enough money");
        }

        // Update power-up inventory
        if (response && response.refreshPowerUp){
          refetechInventory();
          dispatch(decreasePowerUpAction());
        }

        console.log("game-end");

        refetchNotifications();

        // if rushGameState.powerUps is not empty, check if there is still powerUps left in user.inventory

        dispatch(endGameAction());
        rushChartRef.current.end();
        refetechMoney();

      });

      socket.on("game-extend", (response:any) => {

        if (!response.isGameExtended){
          toastCustomError("Game Time could not be extended");
        } else {
          extendGameDispatch(response.runningSession);
          refetchUser();
        }

      });

    }

    return () => {
      // Remove listeners
      socket.off("game-start");
      socket.off("game-end");
      socket.off("game-tick");
      socket.off("game-extend");
    }

  }, [user]);

  // Using useReducer with the external amountReducer
  const [rushGameState, dispatch] = useImmerReducer(rushGameReducer, rushGameInitialState);

  // Action to dispatch Amount
  const updateAmountDispatch = (newAmount: number) => dispatch(setAmountAction(newAmount));

  // Action to dispatch Leverage
  const updateLeverageDispatch = (newLeverage: number) => dispatch(setLeverageAction(newLeverage));

  const addPowerUpDispatch = (newPowerUp: GroupInventoryItem) => dispatch(addPowerUpAction(newPowerUp));

  const removePowerUpDispatch = () => dispatch(removePowerUpAction());

  const extendGameDispatch = (extendedSession: any) => {
    dispatch(extendGameAction(extendedSession));
  }

  const startGameDispatch = (isTradingUp: boolean) => {
    dispatch(startGameAction(isTradingUp, socket));
    rushChartRef.current.start(isTradingUp);
  };

  const loadGameDispatch = (rushGameState: RushGameState) => {
    dispatch(loadGameAction(rushGameState));
    rushChartRef.current.load(rushGameState);
  };

  const tickGameDispatch = (tradePrice: number) => {
    dispatch(tickGameAction(tradePrice));
  }

  const endGameDispatch = () => {
    const currentValue = rushChartRef.current.getCurrentValue();
    rushChartRef.current.end();
    socket.emit("end-game", {currentValue: currentValue});
  };

  const extendGame = () => {
    socket.emit("extend-game");
  };

  return (
    <Layout classes={"min-h-screen justify-start !pt-[73px]"}>

      {/* {!isUserLoading && user &&<RushGameSocket userId={user._id} ref={gameSocketRef}/>} */}

      {/* Secondary Navigation */}
      <div className="py-2">
        <SecondaryNavigation
          navItems={[{ path: "/rush-game", name: "Rush Game" }]}
        />
      </div>

      {/* FIXED: 145px */}
      {/* Rush Chart */}
      <div className='w-screen relative h-[145px] overflow-hidden mb-4'>
        <p className='absolute top-1 left-2 text-white text-xs font-normal'>Bitcoin (BTC)</p>
        <RushChart ref={rushChartRef}/>
      </div>

      {/* <div className="w-[100vw] bg-black h-[146px] flex justify-center center">
        <p className="text-white">RT CHART</p>
      </div> */}

      {/* Game Elements */}
      <div className='flex flex-col w-full max-w-md gap-2'>

        <div className='flex grow gap-3 h-[20vh] min-h-[174px]'>

          <div className='h-full w-1/2 flex center'>

            <div className={`w-full h-full ${rushGameState.isPlaying ? 'hidden' : 'visible'} `}>
              {/* Trade Amount Component */}
              <TradingAmount tradeAmount={rushGameState.tradeAmount} setAmount={updateAmountDispatch} />
            </div>

            <div className={`w-full h-full ${rushGameState.isPlaying ? 'visible' : 'hidden'} `}>
              {/* Circular Count Down Amount Component */}
              <CircularCountDown maxTime={rushGameState.countDownMaxTime} currentTime={rushGameState.countDownCurrentTime} isWinning={rushGameState.isWinning} />
            </div>

          </div>

          <div className='h-full w-1/2 flex flex-col gap-3 justify-between'>

            <div className='flex-1'>
              <Leverage isPlaying={rushGameState.isPlaying} leverage={rushGameState.tradeLeverage} setLeverage={updateLeverageDispatch} />
            </div>

            <div className='flex-1'>
              <PowerUpSelector isPlaying={rushGameState.isPlaying} powerUp={rushGameState.tradePowerUp} setPowerUp={addPowerUpDispatch} resetPowerUp={removePowerUpDispatch} />
            </div>

          </div>

        </div>

        <div className="w-full flex flex-col gap-4 py-3 max-w-md mx-auto">
          <GameControls isPlaying={rushGameState.isPlaying} price={rushGameState.gemPrice} startGame={startGameDispatch} endGame={endGameDispatch} extendGame={extendGame} />
        </div>

      </div>

      {/* <div className='flex grow place-content-end pt-1 pb-4 w-full justify-center'>
        <a href="https://shop.ledger.com/pages/ledger-flex?r=9081a6580cae" className="flex w-full max-w-md mx-auto h-32 relative bg-black rounded-xl center place-self-end" target='_blank'>
          <img src={process.env.REACT_APP_STATIC_ASSETS + "/images/ledger/ledger.png"} alt="Ledger" className='h-[128px]' />
        </a>
      </div> */}

      <div className='flex grow place-content-end pt-1 pb-4 w-full justify-center'>
        <a href="https://shop.ledger.com/pages/gift-of-bitcoin?r=9081a6580cae" className="flex w-full max-w-md mx-auto h-32 relative bg-black rounded-xl center place-self-end" target='_blank'>
          <img src={process.env.REACT_APP_STATIC_ASSETS + "/images/ledger/ledger-bitcoin.png"} alt="Ledger" className='h-[128px]' />
        </a>
      </div>

      {/* Game Controls */}
      {/* <div className="w-full flex flex-col gap-4 py-3 max-w-md mx-auto">
        <GameControls isPlaying={rushGameState.isPlaying} price={rushGameState.gemPrice} startGame={startGameDispatch} endGame={endGameDispatch}/>
      </div> */}

      <InitialOnboardingDrawer open={openOnboarding} onOpen={() => { }} onClose={() => { }} />

    </Layout>
  );
};

export {
  RushGamePage
}